import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { ApiCustomer } from '@vend/types'
import {
  FEATURE_CAPTURE_ADDRESS_SELL_SCREEN,
  LRX_30788_OUT_OF_STOCK_FLOW,
} from '../../../../../../constants'
import {
  FulfilmentDetail,
  FulfilmentType,
  isDefinedFulfilmentType,
} from '../../../../../../models/fulfilment'
import {
  useCustomerManager,
  useSaleManager,
} from '../../../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../../../state/hooks'
import {
  selectLineItem,
  setFulfilmentDetails,
  updateLineItem,
} from '../../../../../../state/slices/currentSale/currentSaleSlice'
import {
  markSaleCustomerContactInfoAsReviewed,
  selectOpenFulfilmentPopoverLineItemId,
  setExpandedLineItemIdWithFulfilmentTab,
  setExpandedLineItemIdWithFulfilmentTabPreservingPopover,
} from '../../../../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../../../../util/feature-cache'
import {
  CurrentView,
  useLineItemFulfillmentContext,
} from '../LineItemFulfillmentContext'
import {
  isContactInfoUpdated,
  trimCustomerInfo,
} from '../LineItemFulfillmentUtils'
import { generateAddressDetailsFromCustomer } from '../../fulfillmentHelper'
import { isAllAddressFieldsEmpty } from '../../address/address-validation'
import {
  AddressErrorsStatus,
  useLineItemFulfillmentAddressDetails,
} from './useLineItemFulfillmentAddressDetails'

interface useLineItemFulfillmentReviewContactInfoFlowProps {
  onClose: () => void
  onSelectFulfilmentType?: (type: FulfilmentType) => void
}

export const useLineItemFulfillmentReviewContactInfoFlow = ({
  onClose,
}: useLineItemFulfillmentReviewContactInfoFlowProps) => {
  const {
    setCurrentView,
    currentCustomer,
    originalCustomer,
    setIsLoading,
    onSelectFulfilmentType,
    fulfilmentDetails,
    setFulfilmentDetails: setContextFulfilmentDetails,
  } = useLineItemFulfillmentContext()

  const t = useT()
  const saleManager = useSaleManager()
  const customerManager = useCustomerManager()
  const lineItemId = useAppSelector(selectOpenFulfilmentPopoverLineItemId)
  const lineItem = useAppSelector(selectLineItem(lineItemId))
  const dispatch = useAppDispatch()
  const isCaptureAddressSellScreenEnabled = getFeatureFromCache(
    FEATURE_CAPTURE_ADDRESS_SELL_SCREEN
  )
  const isOutOfStockFlowFeatureEnabled = getFeatureFromCache(
    LRX_30788_OUT_OF_STOCK_FLOW
  )

  const {
    shouldFixSaleAddresses,
    status,
    isFulfillmentAddressNeedToFix,
  } = useLineItemFulfillmentAddressDetails(fulfilmentDetails)

  const handleContinueFromReviewContactInfoView = async (
    customerFulfilment?: FulfilmentDetail
  ) => {
    const lineItemFulfilmentType = lineItem?.fulfilmentType
      ? lineItem?.fulfilmentType
      : FulfilmentType.UNSPECIFIED

    const fulfilmentType = customerFulfilment?.type || lineItemFulfilmentType

    if (currentCustomer && originalCustomer) {
      if (onSelectFulfilmentType) {
        // calling this here in case the fulfilment type is already set from fulfilments tab
        onSelectFulfilmentType(fulfilmentType)
      }
      const isUpdated = isContactInfoUpdated(currentCustomer, originalCustomer)
      if (isUpdated) {
        try {
          trimCustomerInfo(currentCustomer)
          setIsLoading(true)
          const savedCustomer = await customerManager.updateCustomer(
            currentCustomer
          )
          await saleManager.addCustomer(savedCustomer)
          customerFulfilment
            ? handleContinueFromOutOfStockLineItems(customerFulfilment)
            : goToNextView()
        } catch (_) {
          ToastNotifications.negative(
            t('Unable to update customer. Try again.')
          )
        } finally {
          updateFulfilmentAddressDetails(
            customerFulfilment ?? fulfilmentDetails,
            currentCustomer
          )
          setIsLoading(false)
        }
      } else {
        setIsLoading(true)
        await saleManager.addCustomer(currentCustomer)
        updateFulfilmentAddressDetails(
          customerFulfilment ?? fulfilmentDetails,
          currentCustomer
        )
        setIsLoading(false)
        customerFulfilment
          ? handleContinueFromOutOfStockLineItems(customerFulfilment)
          : goToNextView()
      }
    }
  }

  const goToNextView = () => {
    if (isDefinedFulfilmentType(lineItem?.fulfilmentType)) {
      const isAddressEmpty =
        status === AddressErrorsStatus.EMPTYSHIPPING_EMPTYBILLING
      if (
        isCaptureAddressSellScreenEnabled &&
        lineItem?.fulfilmentType === FulfilmentType.DELIVERY &&
        currentCustomer &&
        (shouldFixSaleAddresses || isAddressEmpty)
      ) {
        if (isAddressEmpty) {
          dispatch(
            setFulfilmentDetails([
              { ...fulfilmentDetails, shouldAddNewAddress: true },
            ])
          )
          onClose()
        } else {
          setCurrentView(CurrentView.FIX_SALE_ADDRESSES)
        }
      } else onClose()
    } else {
      setCurrentView(CurrentView.FULFILMENT_METHOD)
    }

    dispatch(markSaleCustomerContactInfoAsReviewed())
  }

  const updateFulfilmentAddressDetails = (
    fulfilmentDetails: FulfilmentDetail,
    currentCustomer: ApiCustomer
  ) => {
    if (
      isCaptureAddressSellScreenEnabled &&
      fulfilmentDetails.type === FulfilmentType.DELIVERY
    ) {
      const newFulfilmentDetails = {
        ...fulfilmentDetails,
        ...generateAddressDetailsFromCustomer(currentCustomer),
      }

      // Update both Redux state and Context state
      const updatedFulfilmentDetail = {
        ...fulfilmentDetails,
        billingAddress: newFulfilmentDetails.billingAddress,
        shippingAddress: newFulfilmentDetails.shippingAddress,
      }

      // Update Redux state
      dispatch(setFulfilmentDetails([updatedFulfilmentDetail]))

      // Update Context state so components reading from context get fresh data
      setContextFulfilmentDetails(updatedFulfilmentDetail)

      if (isOutOfStockFlowFeatureEnabled) saleManager.unfulfilledDelivery()

      return saleManager.updateFulfillmentDetails([newFulfilmentDetails])
    }

    if (
      fulfilmentDetails.type === FulfilmentType.PICKUP &&
      isOutOfStockFlowFeatureEnabled
    )
      saleManager.unfulfilledPickUp()
  }

  const handleContinueFromOutOfStockLineItems = (
    fulfilmentDetails: FulfilmentDetail
  ) => {
    const {
      billingAddress,
      shippingAddress,
    } = generateAddressDetailsFromCustomer(currentCustomer!)
    const customerFulfilmentDetails = {
      ...fulfilmentDetails,
      billingAddress,
      shippingAddress,
    }
    const shouldFixTheAddress = isFulfillmentAddressNeedToFix(
      customerFulfilmentDetails
    )
    const isAddressEmpty =
      isAllAddressFieldsEmpty(shippingAddress) &&
      isAllAddressFieldsEmpty(billingAddress)

    if (
      isCaptureAddressSellScreenEnabled &&
      fulfilmentDetails.type === FulfilmentType.DELIVERY
    ) {
      if (shouldFixTheAddress || isAddressEmpty) {
        dispatch(
          setExpandedLineItemIdWithFulfilmentTabPreservingPopover(lineItemId)
        )
      } else {
        dispatch(setExpandedLineItemIdWithFulfilmentTab(lineItemId))
      }
      if (lineItem)
        // update line time to rerender popover with correct fulfilment type
        dispatch(
          updateLineItem({
            ...lineItem,
            fulfilmentType: fulfilmentDetails.type,
          })
        )

      if (isAddressEmpty) {
        dispatch(
          setFulfilmentDetails([
            { ...customerFulfilmentDetails, shouldAddNewAddress: true },
          ])
        )
        onClose()
      }
    } else {
      onClose()
    }
  }

  return {
    handleContinueFromReviewContactInfoView,
    handleContinueFromOutOfStockLineItems,
  }
}
